import { css } from '@emotion/react';
import { motion } from 'motion/react';
import { useTranslation } from '@frontend/i18n';
import { breakpoints, useMatchMedia } from '@frontend/responsiveness';
import { shell } from '@frontend/shell-utils';
import { theme } from '@frontend/theme';
import { usePortal } from '@frontend/design-system';
import { useShellTheme } from './store';
import { TitleBarButton } from './title-bar-button';

const CustomTrafficLightControls = () => {
  const { t } = useTranslation('base');

  const handleMinimizeWindow = () => {
    shell.emit?.('window:control', 'minimize');
  };

  const handleCloseWindow = () => {
    shell.emit?.('window:control', 'close');
  };

  const handleMaximizeWindow = () => {
    shell.emit?.('window:control', 'maximize');
  };

  return (
    <div
      css={css`
        display: flex;
        align-items: center;
        margin-right: ${theme.spacing(1.5)};
        margin-left: auto;
        gap: ${theme.spacing(0.5)};
      `}
    >
      <TitleBarButton label={t('Minimize Window')} onClick={handleMinimizeWindow} icon='minus' />
      <TitleBarButton
        label={t('Maximize Window')}
        onClick={handleMaximizeWindow}
        icon='full-screen-windows'
        aria-label={t('Maximize')}
      />
      <TitleBarButton label={t('Close Window')} onClick={handleCloseWindow} icon='x' />
    </div>
  );
};

export const MacTitleBarContent = () => {
  return (
    <>
      <div id='weave-shell-title-bar-buttons'></div>
      <div
        id='weave-shell-title-bar-content'
        css={css`
          flex-basis: 450px;
          margin-left: auto;
          margin-right: ${theme.spacing(2)};
        `}
      ></div>
      <TitleBarAction isMac />
    </>
  );
};

const TitleBarContent = () => {
  return (
    <>
      <div id='weave-shell-title-bar-buttons'></div>
      <TitleBarAction />
      <div
        id='weave-shell-title-bar-content'
        css={css`
          flex-basis: 450px;
          margin-left: ${theme.spacing(1)};
        `}
      ></div>

      <CustomTrafficLightControls />
    </>
  );
};

const shrinkButtonTrackingIdBase = 'weave-shell-shrink-button';

const TitleBarAction = ({ isMac }: { isMac?: boolean }) => {
  const { t } = useTranslation('base');
  const isMediumBreakpoint = useMatchMedia({ maxWidth: breakpoints.medium.min });
  const hasWindowShrink = shell?.featureAvailability?.has?.('window-shrink');

  return hasWindowShrink ? (
    <TitleBarButton
      onClick={() => shell?.invoke?.('main:update-options', { width: !isMediumBreakpoint ? 400 : 1500 })}
      label={isMediumBreakpoint ? t('Use Expanded View') : t('Use Compact View')}
      showLabelOnHover
      trackingId={isMediumBreakpoint ? `${shrinkButtonTrackingIdBase}-large` : `${shrinkButtonTrackingIdBase}-small`}
      icon={isMediumBreakpoint ? 'quick-expand' : 'quick-shrink'}
      about={isMediumBreakpoint ? t('Use Expanded View') : t('Use Compact View')}
      css={
        isMac
          ? css`
              margin-right: ${theme.spacing(2)};
            `
          : css`
              margin-left: ${theme.spacing(2)};
            `
      }
    />
  ) : (
    <TitleBarButton
      onClick={() => navigator.clipboard.writeText(window.location.href)}
      label={t('Copy Current URL')}
      showLabelOnHover
      icon='link'
      about={t('Copy Current URL')}
      css={
        isMac
          ? css`
              margin-right: ${theme.spacing(2)};
            `
          : css`
              margin-left: ${theme.spacing(2)};
            `
      }
    />
  );
};

export const ShellTitleBar = () => {
  const { colorObject } = useShellTheme('colorObject');
  const { Portal } = usePortal({
    mountTarget: '#weave-shell-title-bar',
    attributes: {
      style: `
      height: 100%;
      width: 100%;`,
    },
  });
  const { isMac } = shell;

  return (
    <Portal>
      <motion.div
        css={css`
          display: flex;
          align-items: center;
          min-height: 42px;
          height: 42px;
          width: 100%;
          margin-bottom: auto;
          transition: background 0.6s ease-in-out;
        `}
        initial={{ background: 'linear-gradient(135.55deg, #C2F4F1 25.85%, #89E1DB 107.81%)' }}
        animate={{ background: colorObject.shellColor }}
      >
        {isMac ? <MacTitleBarContent /> : <TitleBarContent />}
      </motion.div>
    </Portal>
  );
};
